import React, { useMemo, useRef, useCallback } from 'react';
import { useEffect, useState } from 'react';
import type { CallBackProps, Step } from 'react-joyride';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import { Button } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import type { OnboardingStep } from './types';
import {
  useOnboardingMessages,
  useUpdateOnboardingMessage,
  useRevisitOnboardingMessages,
} from './hooks/useOnboardingMessages';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { transformOnboardingData } from './utils/onboardingTourUtils';
import { useRTL } from 'configs/constants';

interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
}

interface OnboardingTourProps {
  start: boolean;
  setStartTour: (value: boolean) => void;
  onTourEnd: () => void;
  isPaused?: boolean;
  setIsTourPaused: (value: boolean | ((prev: boolean) => boolean)) => void;
  useRevisitMessages?: boolean;
}

const OnboardingTour = ({
  start,
  setStartTour,
  onTourEnd,
  isPaused = false,
  setIsTourPaused,
  useRevisitMessages = false,
}: OnboardingTourProps) => {
  const { t } = useTranslation();
  const { data: onboardingMessages = [] } = useOnboardingMessages();
  const { data: revisitMessages = [] } = useRevisitOnboardingMessages(start && useRevisitMessages);
  const { mutate: updateMessageStatus } = useUpdateOnboardingMessage();
  const [progress, setProgress] = useState<number>(1);
  const isRTL = useRTL();

  // Draggable button state
  const [buttonPosition, setButtonPosition] = useState({ x: 32, y: 128 }); // Default: left-8, bottom-32
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const messages = useRevisitMessages ? revisitMessages : onboardingMessages;

  const activeMessages = useMemo(
    () => transformOnboardingData(messages, isRTL).filter((message: OnboardingStep) => message.active) || [],
    [messages, isRTL],
  );
  const totalSteps = activeMessages.length;

  const steps = useMemo(() => {
    if (!activeMessages.length) return [];

    return activeMessages.map((step: OnboardingStep) => {
      // Check if target element exists in DOM
      const targetExists = step.target === 'body' || (step.target && document.querySelector(step.target));

      return {
        content: (
          <div className="flex flex-col text-left text-white rtl:text-right">
            <p className="footnote-dec mb-3">
              {t('onboarding.floater.step')} {progress}/{totalSteps}
            </p>
            <p className="h3-medium mb-2">{step.titleText}</p>
            <p className="h5-regular">{step.additionText}</p>
          </div>
        ),
        styles: {
          options: {
            width: 320,
          },
        },
        placement: targetExists ? step.placement || 'center' : 'center',
        target: targetExists ? step.target || 'body' : 'body',
        disableBeacon: true,
        hideBackButton: progress === 1,
        spotlightPadding: step.spotlightPadding,
      };
    });
  }, [activeMessages, totalSteps, progress, t]);

  const [state, setState] = useState<State>({
    run: start,
    stepIndex: 0,
    steps,
  });

  useEffect(() => {
    if (start) {
      setState((prevState) => ({
        ...prevState,
        run: true,
        stepIndex: 0,
      }));
    }
  }, [start]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      run: !isPaused && start,
    }));
  }, [isPaused, start]);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!buttonRef.current) return;

    setIsDragging(true);
    setHasDragged(false); // Reset drag flag
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !buttonRef.current) return;

      setHasDragged(true); // Mark that we have dragged
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const buttonRect = buttonRef.current.getBoundingClientRect();

      let newX = e.clientX - dragOffset.x;
      let newY = viewportHeight - (e.clientY - dragOffset.y) - buttonRect.height;

      // Boundary constraints - more space on right side to avoid scrollbar
      newX = Math.max(8, Math.min(newX, viewportWidth - buttonRect.width - 24));
      newY = Math.max(8, Math.min(newY, viewportHeight - buttonRect.height - 8));

      setButtonPosition({ x: newX, y: newY });
    },
    [isDragging, dragOffset],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!buttonRef.current) return;

    setIsDragging(true);
    setHasDragged(false); // Reset drag flag
    const touch = e.touches[0];
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !buttonRef.current) return;

      setHasDragged(true); // Mark that we have dragged
      const touch = e.touches[0];
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const buttonRect = buttonRef.current.getBoundingClientRect();

      let newX = touch.clientX - dragOffset.x;
      let newY = viewportHeight - (touch.clientY - dragOffset.y) - buttonRect.height;

      // Boundary constraints - more space on right side to avoid scrollbar
      newX = Math.max(8, Math.min(newX, viewportWidth - buttonRect.width - 24));
      newY = Math.max(8, Math.min(newY, viewportHeight - buttonRect.height - 8));

      setButtonPosition({ x: newX, y: newY });
      e.preventDefault();
    },
    [isDragging, dragOffset],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add event listeners for mouse and touch events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, type } = data;

    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      // Only update status for regular onboarding tours, not for revisit tours
      if (!useRevisitMessages) {
        if (status === STATUS.SKIPPED && type === 'tour:end') {
          // Mark all remaining messages as skipped
          const remainingMessages = activeMessages.slice(index);

          // Process each message sequentially to avoid overwhelming the API
          const processMessages = async () => {
            for (const message of remainingMessages) {
              await updateMessageStatus({
                onboardingMessageId: message.id,
                status: 'READ',
              });
            }
          };

          processMessages().catch(console.error);
        } else if (status === STATUS.FINISHED) {
          // Mark the last step as read
          const lastMessage = activeMessages[activeMessages.length - 1];
          if (lastMessage) {
            updateMessageStatus({
              onboardingMessageId: lastMessage.id,
              status: 'READ',
            });
          }
        }
      }

      setState((prevState) => ({ ...prevState, run: false, stepIndex: 0 }));
      setStartTour(false);
      onTourEnd();
    } else if (([EVENTS.STEP_BEFORE] as string[]).includes(type)) {
      setProgress(index + 1);
    } else if (
      status === STATUS.RUNNING &&
      type === 'step:after' &&
      data.action === 'next' &&
      index < activeMessages.length - 1 && // Only for non-final steps
      !useRevisitMessages // Only update status for regular onboarding tours
    ) {
      // Mark current step as read only when moving forward and not on the last step
      const currentMessage = activeMessages[index];
      if (currentMessage) {
        updateMessageStatus({
          onboardingMessageId: currentMessage.id,
          status: 'READ',
        });
      }
    }
  };

  if (!activeMessages.length) return null;

  return (
    <>
      <Joyride
        continuous
        callback={handleJoyrideCallback}
        run={state.run}
        steps={steps}
        scrollToFirstStep
        disableCloseOnEsc
        disableOverlayClose
        hideCloseButton
        showSkipButton
        floaterProps={{
          styles: {
            floater: {
              filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
            },
          },
        }}
        styles={{
          options: {
            backgroundColor: '#0052CC',
            zIndex: 40,
            arrowColor: '#0052CC',
            overlayColor: '#11111180',
          },
          tooltip: {
            padding: '12px',
            borderRadius: '8px',
          },
          tooltipContent: {
            padding: '0px',
          },
          buttonNext: {
            backgroundColor: 'transparent',
            color: '#FFFFFF',
            marginLeft: '16px',
          },
          buttonBack: {
            color: '#FFFFFF',
          },
          buttonSkip: {
            color: '#FFFFFF',
          },
          spotlight: {
            borderRadius: '8px',
          },
          overlay: {
            zIndex: 40,
            pointerEvents: 'auto',
          },
        }}
        locale={{
          back: t('onboarding.floater.back'),
          next: t('onboarding.floater.next'),
          skip: t('onboarding.floater.skipAll'),
          last: t('onboarding.floater.startExploring'),
        }}
      />
      {start &&
        ReactDOM.createPortal(
          <div
            ref={buttonRef}
            className={`fixed z-[9999] pointer-events-auto select-none ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            style={{
              left: `${buttonPosition.x}px`,
              bottom: `${buttonPosition.y}px`,
              transform: isDragging ? 'scale(1.05)' : 'scale(1)',
              transition: isDragging ? 'none' : 'transform 0.2s ease',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <Button
              color="primary"
              variant="outlined"
              icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={(e) => {
                // Only trigger click if we haven't dragged
                if (!hasDragged) {
                  setIsTourPaused((prev: boolean) => !prev);
                }
                e.stopPropagation();
              }}
              className={`flex items-center justify-center shadow-lg transition-shadow duration-200 ${
                isDragging ? 'shadow-xl' : 'hover:shadow-xl'
              }`}
            >
              {isPaused ? t('onboarding.play') : t('onboarding.pause')}
            </Button>
          </div>,
          document.body,
        )}
    </>
  );
};

export default OnboardingTour;
